import { Link } from '@usehatchapp/material';
import {
  Button,
  Form,
  ForwardedInput,
  Label,
  ToggleSwitch,
} from 'hatch-components';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useConnect } from 'redux-bundler-hook';
import DrawerBody from 'root/components/molecules/Drawer/components/DrawerBody';
import DrawerError from 'root/components/molecules/Drawer/components/DrawerError';
import DrawerFooter from 'root/components/molecules/Drawer/components/DrawerFooter';
import FormMaskedInput from 'root/components/molecules/FormMaskedInput';
import { FormSelect } from 'root/components/molecules/FormSelect';
import { StyledHelperText } from 'root/components/molecules/Select/styles';
import { COMPLIANCE_10LDC_FORM_URL } from 'root/stores/routes/APP_URLS';
import { timezoneOptions } from 'utils/getTimezone';

import {
  StyledObservesDaylightSavingsTimeLabel,
  StyledObservesDaylightSavingsTimeRow,
} from './styles';

const beNoNumbersFoundErrorMessage = 'No phone numbers found in area code.';
const beInvalidAreaCodeErrorMessage = 'Invalid area code provided.';

const CreateWorkspaceForm = ({ onClose, form }) => {
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');
  const {
    doCreateBoard,
    doCreateNotificationMessage,
    boardsSortedByArchived,
    teamMemberOptionsWithoutChatbot: teamMemberOptions,
    currentOrganizationId,
    isCreatingBoard,
    twilioRegisteredBrandOptions,
    chatbotTeamMemberOptions,
  } = useConnect(
    'doCreateBoard',
    'doCreateNotificationMessage',
    'selectBoardsSortedByArchived',
    'selectTeamMemberOptionsWithoutChatbot',
    'selectCurrentOrganizationId',
    'selectIsCreatingBoard',
    'selectTwilioRegisteredBrandOptions',
    'selectChatbotTeamMemberOptions',
  );
  const {
    register,
    control,
    handleSubmit,
    errors,
    formState,
    setError,
    setValue,
    watch,
  } = form;

  const { isDirty } = formState;
  const watchObservesDaylightSavings = watch('observesDaylightSavings');
  const complianceValue = watch('compliance10dlc');

  useEffect(() => {
    if (twilioRegisteredBrandOptions.length === 1) {
      setValue('compliance10dlc', twilioRegisteredBrandOptions[0], {
        shouldDirty: false,
      });
    }
  }, [twilioRegisteredBrandOptions, setValue]);

  const done = (res, { data } = {}) => {
    const bodyErrors = res?.response?.errors || [];
    if (bodyErrors.length) {
      if (
        bodyErrors.some(
          (msg) =>
            (typeof msg === 'string' && msg?.includes('code 2:')) ||
            (msg?.detail && msg?.detail === beNoNumbersFoundErrorMessage),
        )
      ) {
        return setError('areaCode', {
          message:
            'Area Code is full, please choose another or contact support',
        });
      }

      if (
        bodyErrors.some(
          (msg) => msg?.detail && msg?.detail === beInvalidAreaCodeErrorMessage,
        )
      ) {
        return setError('areaCode', {
          message: beInvalidAreaCodeErrorMessage,
        });
      }

      return setSubmitErrorMessage(bodyErrors[0]?.detail || bodyErrors[0]);
    }

    if (!data && res?.message) {
      return doCreateNotificationMessage({
        message: `Error: '${res.message}'`,
        type: 'default',
      });
    }

    doCreateNotificationMessage({
      message: `Workspace '${data.name}' has been created.`,
      type: 'success',
    });
    return onClose({ ignore: true });
  };

  const onSubmit = (data) => {
    if (isCreatingBoard) {
      return;
    }

    const {
      name,
      areaCode,
      assignedMembers,
      compliance10dlc,
      department,
      timezone,
      observesDaylightSavings,
      initials,
    } = data;

    const dept = boardsSortedByArchived.find(
      (board) => board.mongoId === department.value,
    )?.workspaceGroup.locationId;

    doCreateBoard(
      {
        data: {
          areaCode,
          departmentId: dept || null,
          name,
          nickname: initials,
          teamMemberIDs: [
            ...(assignedMembers || []),
            // TODO: Move this chatbot adding logic to backend asap (UXP-375)
            ...chatbotTeamMemberOptions,
          ]
            .map(({ value }) => value)
            .filter(Boolean),
          timezone: {
            observes_daylight_savings: observesDaylightSavings,
            offset_hours: parseInt(timezone.value, 10),
          },
          twilioRegistrationID: compliance10dlc.value,
        },
        organizationId: currentOrganizationId,
      },
      done,
    );
  };

  const url = COMPLIANCE_10LDC_FORM_URL.replace(
    '[[organizationID]]',
    currentOrganizationId,
  );

  const emptyDepartment = [
    {
      label: 'None',
      value: '',
    },
  ];

  const departmentOptions = [
    ...emptyDepartment,
    ...boardsSortedByArchived.map((board) => ({
      label: board.active ? board.name : `(Archived) ${board.name}`,
      value: board.mongoId,
    })),
  ];

  return (
    <>
      {submitErrorMessage ? <DrawerError message={submitErrorMessage} /> : null}
      <DrawerBody>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Label htmlFor="10dlc" required error={!!errors?.compliance10dlc}>
            Company
          </Label>
          <FormSelect
            control={control}
            data-test-id="10dlc-brand"
            fullWidth
            id="10dlc"
            name="compliance10dlc"
            options={twilioRegisteredBrandOptions}
            value={complianceValue}
            error={!!errors?.compliance10dlc}
            helperText={errors?.compliance10dlc?.message || ''}
            isDisabled={twilioRegisteredBrandOptions.length === 1}
            ref={register('compliance10dlc', {
              required: 'A company is required',
            })}
            style={{ marginBottom: 0 }}
          />
          <StyledHelperText>
            Required for 10DLC brand registration. Visit{' '}
            <Link data-test-id="goto-compliance-page-link" href={url}>
              Account &gt; Settings &gt; Compliance
            </Link>{' '}
            to register you companies.
          </StyledHelperText>

          <Label error={!!errors?.name} htmlFor="name" required>
            Workspace Name
          </Label>
          <ForwardedInput
            data-test-id="aba54f4c-f4ed-4e2f-9198-8d02bb29575b"
            error={!!errors?.name}
            fullWidth
            helperText={errors?.name?.message}
            id="name"
            name="name"
            ref={register({ required: 'A workspace name is required' })}
          />

          <Label error={!!errors?.initials} htmlFor="initials" required>
            Initials
          </Label>
          <ForwardedInput
            data-test-id="6587fec6-4ac5-4319-9f52-3e58fca4c921"
            error={!!errors?.initials}
            fullWidth
            helperText={errors?.initials?.message}
            id="initials"
            maxLength="5"
            name="initials"
            ref={register({
              maxLength: 5,
              required: 'An abbreviation for this workspace is required',
            })}
          />

          <Label error={!!errors?.areaCode} htmlFor="areaCode" required>
            Area Code
          </Label>
          <FormMaskedInput
            control={control}
            data-test-id="34c3e2a9-4a35-43cc-8401-78a3d7d166e7"
            error={!!errors?.areaCode}
            fullWidth
            helperText={errors?.areaCode?.message}
            id="areaCode"
            maskFn={(v) => v.substring(0, 3)}
            max="989"
            min="201"
            name="areaCode"
            rules={{
              max: { message: 'A valid area code is required', value: 989 },
              maxLength: 3,
              min: { message: 'A valid area code is required', value: 201 },
              required: 'A valid area code is required',
            }}
            type="number"
          />

          <Label htmlFor="timezone" required>
            Time Zone
          </Label>
          <FormSelect
            control={control}
            data-test-id=""
            fullWidth
            id="timezone"
            name="timezone"
            options={timezoneOptions}
          />

          <StyledObservesDaylightSavingsTimeRow fullWidth>
            <ToggleSwitch
              data-test-id="ca997f61-38f5-4a63-b6c8-11c345210335"
              enabled={watchObservesDaylightSavings}
              id="observesDaylightSavings"
              name="observesDaylightSavings"
              onChange={() => {
                setValue(
                  'observesDaylightSavings',
                  !watchObservesDaylightSavings,
                  { shouldDirty: true },
                );
              }}
              ref={register({ name: 'observesDaylightSavings' })}
            />
            <StyledObservesDaylightSavingsTimeLabel>
              Observes Daylight Savings Time
            </StyledObservesDaylightSavingsTimeLabel>
          </StyledObservesDaylightSavingsTimeRow>

          <Label error={!!errors?.members} htmlFor="members">
            Assigned Members
          </Label>
          <FormSelect
            control={control}
            data-test-id="121bd1d0-3f47-46cd-96ea-237ae41169b1"
            error={!!errors?.members}
            fullWidth
            helperText={errors?.members?.message}
            id="members"
            isMulti
            name="assignedMembers"
            options={teamMemberOptions}
          />

          <Label htmlFor="department" required>
            Inherit CRM or Lead Integrations from Another Workspace
          </Label>
          <FormSelect
            control={control}
            data-test-id="create-workspace-form-shared-department"
            fullWidth
            error={!!errors?.department}
            helperText={errors?.department?.message}
            id="department"
            name="department"
            options={departmentOptions}
            rules={{ required: 'Please select a value' }}
          />
          <StyledHelperText>
            This will link workspaces that should share the same customer
            contact integrations. This cannot be changed later.
          </StyledHelperText>
        </Form>
      </DrawerBody>
      <DrawerFooter>
        <Button
          data-test-id="1262a44b-6809-441a-af95-40e5985b1a64"
          disabled={!isDirty}
          fetching={isCreatingBoard}
          minWidth={90}
          onClick={handleSubmit(onSubmit)}
          size="sm"
          variant="primary"
        >
          Save
        </Button>
      </DrawerFooter>
    </>
  );
};

CreateWorkspaceForm.propTypes = {
  form: PropTypes.any, // eslint-disable-line
  onClose: PropTypes.func.isRequired,
};

CreateWorkspaceForm.defaultProps = {
  form: {},
};

export default CreateWorkspaceForm;
